import { useCallback, useContext, useEffect, useMemo, useRef } from "react"
import { ThemeContext } from "styled-components"

import { useResizeListener } from "./useResizeListener"
/**
 * Fires an event if break point smaller than desired
 *
 * @param {BreakPointParams}
 */
export function useBreakpointListener({
  skip,
  breakPointName,
  onWithinBreak,
  onBiggerThanBreak,
  debounceTimeout = 400,
}) {
  const theme = useContext(ThemeContext)
  const breakPointRef = useRef()
  const [breakPointsList, breakPointMap, breakValue] = useMemo(() => {
    const list = []
    const map = {}
    let targetValue = 0
    Object.entries(theme.breakpoints).forEach(([key, value]) => {
      const numberValue = parseInt(value)
      map[numberValue] = key
      list.push(numberValue)
      if (key === breakPointName) {
        targetValue = numberValue
      }
    })
    list.sort((a, b) => a - b)
    return [list, map, targetValue]
  }, [theme.breakpoints, breakPointName])

  const checkBreakPoint = useCallback(() => {
    const currentSize = window.innerWidth
    const litsToUse = breakPointsList.slice()
    const breaks = [litsToUse.shift()]
    litsToUse.some((size) => {
      if (size > currentSize) {
        return true
      }
      breaks.push(size)
      return false
    })
    const currentBreakPoint = breaks.pop() || litsToUse.pop()

    const name = breakPointMap[currentBreakPoint]

    if (Boolean(currentBreakPoint) && currentBreakPoint <= breakValue) {
      if (Boolean(onWithinBreak)) {
        const changed = !breakPointRef.current
        breakPointRef.current = true
        onWithinBreak({
          value: currentBreakPoint,
          name,
          isDesired: breakPointName === name,
          changed,
        })
      }
    } else if (Boolean(onBiggerThanBreak)) {
      const changed = breakPointRef.current === true
      breakPointRef.current = false
      onBiggerThanBreak({
        value: currentBreakPoint,
        name,
        changed,
      })
    }
  }, [breakPointsList, breakPointMap, breakValue, onWithinBreak, onBiggerThanBreak, breakPointName])
  useResizeListener({ onResize: checkBreakPoint, skip, debounceTimeout })
  useEffect(() => {
    if (skip) {
      return
    }
    checkBreakPoint()
  }, [skip, checkBreakPoint])
}
/**
 *
 * @typedef {Object} onSmallerArgs
 *
 * @property {number} value the value of the breakpoint
 * @property {string} name the name of the current break point
 * @property {boolean} isDesired whether the breakpoint is exactly the one being listened to
 * @property {boolean} changed If true means we were not on the break point and now we are
 */

/**
 * Callback for adding two numbers.
 *
 * @callback onSmallerCallback
 * @param {onSmallerArgs} args
 */
/**
 * Callback for adding two numbers.
 *
 * @callback onBiggerCallBack
 * @param {onBiggerArgs} args
 */
/**
 *
 * @typedef {Object} onBiggerArgs
 *
 * @property {number} value the value of the breakpoint
 * @property {string} name the name of the current break point
 * @property {boolean} changed If true means we were on the breakpoint and now we are not
 */

/**
 * @typedef {Object} BreakPointParams
 * @property {boolean} skip if true no listener will be applied
 * @property {string} breakPointName the name of the breakpoint to listen
 * @property {onSmallerCallback} onWithinBreak will be called everytime is smaller than break
 * @property {onBiggerCallBack} onBiggerThanBreak will be called everytime is bigger than break
 * @property {number} debounceTimeout the timeout for the debounce listeners, defaults to 400
 */
